import React, {FC, useMemo, useState} from "react";
import {gql, useMutation} from "@apollo/client";
import {Alert, Button, Form, Input} from "antd";
import {useHistory} from "react-router";
import i18n from "../../services/i18n";
import {AuthenticateDocument, useGetCompanyInfoQuery} from "../../generated/graphql";
import {URL_ASSISTANT_HOME} from "../../models/url";
import {assistantApolloClient} from "./AssistantApolloWrapper";
import {useAssistantTheme} from "./AssistantThemeContext";
import styles from "./assistantApp.less";

const LOGIN_WITH_PASSWORD = gql`
    mutation LoginWithPassword($loginInput: PasswordLoginInputType!) {
        loginWithPassword(loginInput: $loginInput) {
            id
            email
            roles
            hasProfile
        }
    }
`;

type LoginFormValues = {
    email: string;
    password: string;
};

const AssistantLogin: FC = () => {
    const history = useHistory();
    const {resolvedTheme} = useAssistantTheme();
    const [errorMessage, setErrorMessage] = useState<string>();
    const {data: companyInfoData} = useGetCompanyInfoQuery({fetchPolicy: "cache-first"});

    const logo = companyInfoData?.companyInfo?.companySettings?.logo;
    const companyName = companyInfoData?.companyInfo?.name;

    const initialValues = useMemo<LoginFormValues>(() => {
        return {email: "", password: ""};
    }, []);

    const [loginWithPassword, {loading}] = useMutation(LOGIN_WITH_PASSWORD);

    const themeClass = resolvedTheme === "dark" ? styles["assistant-theme-dark"] : styles["assistant-theme-light"];

    return (
        <div className={`${themeClass} ${styles.assistantLoginRoot}`}>
            <div className={styles.assistantLoginCard}>
                {/* Logo */}
                <div className={styles.assistantLoginLogo}>
                    {logo ? (
                        <img
                            src={`data:image/png;base64,${logo}`}
                            alt={companyName || "Logo"}
                            className={styles.assistantLoginLogoImg}
                        />
                    ) : companyName ? (
                        <div style={{fontSize: 24, fontWeight: 700, color: "var(--aa-text-primary)"}}>{companyName}</div>
                    ) : null}
                </div>

                <div className={styles.assistantLoginTitle}>{"Assistenten Login"}</div>
                <div className={styles.assistantLoginSubtitle}>
                    {"Bitte melde dich mit deiner Arbeits-E-Mail und deinem Passwort an."}
                </div>

                {errorMessage ? (
                    <div style={{marginBottom: 16}}>
                        <Alert
                            type="error"
                            message={i18n.containers.login.Login.invalidEmail()}
                            description={errorMessage}
                            showIcon
                        />
                    </div>
                ) : null}

                <Form<LoginFormValues>
                    layout="vertical"
                    initialValues={initialValues}
                    onFinish={async (values) => {
                        setErrorMessage(undefined);
                        try {
                            await loginWithPassword({
                                variables: {
                                    loginInput: {
                                        email: values.email.trim(),
                                        password: values.password,
                                    },
                                },
                            });

                            const authResult = await assistantApolloClient.query({
                                query: AuthenticateDocument,
                                fetchPolicy: "network-only",
                            });

                            if (!authResult.data?.authenticate?.id) {
                                setErrorMessage("Anmeldung fehlgeschlagen.");
                                return;
                            }

                            history.replace(URL_ASSISTANT_HOME);
                        } catch (error) {
                            setErrorMessage(error instanceof Error ? error.message : String(error));
                        }
                    }}
                >
                    <Form.Item
                        label={i18n.containers.login.Login.emailPlaceholder()}
                        name="email"
                        rules={[{required: true, message: i18n.containers.login.Login.invalidEmail()}]}
                    >
                        <Input
                            autoComplete="username"
                            size="large"
                            style={{borderRadius: 8, height: 48}}
                        />
                    </Form.Item>

                    <Form.Item
                        label={"Passwort"}
                        name="password"
                        rules={[{required: true, message: "Bitte Passwort eingeben."}]}
                    >
                        <Input.Password
                            autoComplete="current-password"
                            size="large"
                            style={{borderRadius: 8, height: 48}}
                        />
                    </Form.Item>

                    <Button
                        type="primary"
                        htmlType="submit"
                        block
                        size="large"
                        loading={loading}
                        style={{borderRadius: 10, height: 50, fontSize: 16, marginTop: 8}}
                    >
                        {i18n.containers.login.Login.button()}
                    </Button>
                </Form>
            </div>
        </div>
    );
};

export default AssistantLogin;
